import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Company, SignupRequest } from '../types';
import { authApi } from '../services/api';
import api from '../services/api';

interface AuthContextType {
  user: User | null;
  currentCompany: Company | null;
  availableCompanies: Company[];
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: SignupRequest) => Promise<void>;
  logout: () => void;
  switchCompany: (companyId: string) => void;
  canAccess: (permission: string) => boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isFreePlan: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isFreePlan = currentCompany?.subscription_name === 'Free';

  const isAuthenticated = !!user && !!token;
  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  // Verificar token ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Configurar token no axios
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Verificar se o token ainda é válido
          const response = await authApi.verifyToken();
          console.log('Token verification response:', response);

          setToken(storedToken);
          setUser(response.user);
          const company = {
            id: response.user.company_id,
            name: response.user.company_name,
            slug: response.user.company_slug,
            email: response.user.email,
            subscription_name: response.user.subscription_name
          }
          setCurrentCompany(company);
          setAvailableCompanies(response.availableCompanies || []);
        } catch (error) {
          // Token inválido, limpar dados
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('currentCompany');
          delete api.defaults.headers.common['Authorization'];
        }
      }

      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password });

      setToken(response.token);
      setUser(response.user);

      const company = {
        id: response.user.company?.id || '',
        name: response.user.company?.name || '',
        slug: response.user.company?.slug || '',
        email: response.user.company?.email || '',
        subscription_name: response?.user?.company?.subscription_name || ''
      }

      setCurrentCompany(company);
      setAvailableCompanies(response.availableCompanies || []);
      console.log("RESPONSE:::", response)

      // Configurar token no axios
      api.defaults.headers.common['Authorization'] = `Bearer ${response.token}`;

      // Armazenar no localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('currentCompany', JSON.stringify(company));
    } catch (error) {
      throw error;
    }
  };

  const signup = async (data: SignupRequest) => {
    try {
      const response = await authApi.signup(data);
      console.log("SIGNUP RESPONSE:::", response)

      setToken(response.token);
      setUser(response.user);

      const company = {
        id: response.user.company?.id || '',
        name: response.user.company?.name || '',
        slug: response.user.company?.slug || '',
        email: response.user.company?.email || '',
        subscription_name: response?.user?.company?.subscription_name || ''
      }

      setCurrentCompany(company);
      setAvailableCompanies([company]);

      // Configurar token no axios
      api.defaults.headers.common['Authorization'] = `Bearer ${response.token}`;

      // Armazenar no localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('currentCompany', JSON.stringify(company));
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setCurrentCompany(null);
    setAvailableCompanies([]);

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentCompany');

    delete api.defaults.headers.common['Authorization'];
  };

  const switchCompany = (companyId: string) => {
    const company = availableCompanies.find(c => c.id === companyId);
    if (company) {
      setCurrentCompany(company);
      localStorage.setItem('currentCompany', JSON.stringify(company));
    }
  };

  const canAccess = (permission: string): boolean => {
    if (!user) return false;

    // Owner tem acesso a tudo
    if (user.role === 'owner') return true;

    // Admin tem acesso a quase tudo, exceto algumas configurações sensíveis
    if (user.role === 'admin') {
      const restrictedPermissions = ['billing', 'delete_company', 'manage_owners'];
      return !restrictedPermissions.includes(permission);
    }

    // Member tem acesso limitado
    const memberPermissions = ['view_dashboard', 'view_wheels', 'view_products', 'view_spins'];
    return memberPermissions.includes(permission);
  };

  const value: AuthContextType = {
    user,
    currentCompany,
    availableCompanies,
    token,
    isAuthenticated,
    isLoading,
    login,
    signup,
    logout,
    switchCompany,
    canAccess,
    isOwner,
    isAdmin,
    isFreePlan
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};